import type { OfficeType } from '@/hooks/use-org-profile';
import type { FeatureGateAccess, FeatureGateKey } from '@/lib/feature-gate-registry';

/**
 * Gates a Corporate office never sees, whatever the platform gate says: the way into ministerial
 * representations (register, settings, create actions), the electorate view of People, the
 * Explorer map with its analysis, buildings and address layers, and the Ads Explorer with its
 * sharing and scoring. Records are untouched. The three `minrep_*` letter gates are not here: they
 * shape a letter that already exists, and switching them off would change how an existing one
 * opens and saves.
 *
 * The mask only turns gates off in the answer, so it never grants what the platform gate
 * withholds. A reader that infers something from a gate being off still sees the change, so each
 * reader of a masked gate was checked (`useAuditedPersonOpen` asks the server for a Corporate
 * office rather than treat the masked gate as an unaudited office).
 */
export const CORPORATE_HIDDEN_FEATURES = [
  'ministerial_representations',
  'electorate_crm',
  'ads_explorer',
  'ads_public_sharing',
  'ads_exposure_score',
  'explorer',
  'explorer_analysis',
  'explorer_buildings',
  'explorer_address_panel',
] as const satisfies readonly FeatureGateKey[];

export function maskFeatureAccessForOfficeType(
  access: FeatureGateAccess,
  officeType: OfficeType,
): FeatureGateAccess {
  if (officeType !== 'corporate') {
    return access;
  }
  const masked = { ...access };
  for (const key of CORPORATE_HIDDEN_FEATURES) {
    masked[key] = false;
  }
  return masked;
}
